import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_BASE_URL } from "../config";
import { setError } from "./errorSlice";
const initialState = {
  periods: [],
  schoolDays: [],
  loading: false,
  error: null,
};

export const fetchPeriods = createAsyncThunk(
  "schoolDay/fetchPeriods",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      const response = await fetch(`${API_BASE_URL}/api/Period`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch periods");
      }
      return await response.json();
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSchoolDays = createAsyncThunk(
  "schoolDay/fetchSchoolDays",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      const response = await fetch(`${API_BASE_URL}/api/SchoolDay`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch school days");
      }
      return await response.json();
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const saveSchoolDay = createAsyncThunk(
  "schoolDay/saveSchoolDay",
  async ({ schoolDayName, periodID }, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      const response = await fetch(`${API_BASE_URL}/api/SchoolDay`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ schoolDayName, periodID }),
      });

      if (!response.ok) {
        throw new Error("Failed to save school day");
      }
      return await response.json();
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const updateSchoolDay = createAsyncThunk(
  "schoolDay/updateSchoolDay",
  async (
    { id, schoolDayName, periodID, scDayID },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      const response = await fetch(`${API_BASE_URL}/api/SchoolDay/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ schoolDayName, periodID, scDayID }),
      });

      if (!response.ok) {
        throw new Error("Failed to update school day");
      }
      return await response.json();
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSchoolDay = createAsyncThunk(
  "schoolDay/deleteSchoolDay",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      const response = await fetch(`${API_BASE_URL}/api/SchoolDay/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete school day");
      }
      return id;
    } catch (error) {
       dispatch(setError(error.message));
       return rejectWithValue(error.message);
    }
  }
);

const schoolDaySlice = createSlice({
  name: "schoolDay",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPeriods.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPeriods.fulfilled, (state, action) => {
        state.loading = false;

        // Sort periods by startTime (chronological), then by periodName
        state.periods = action.payload.sort((a, b) => {
          // Convert start times to minutes for accurate comparison
          const timeToMinutes = (timeStr) => {
            const [hours, minutes] = timeStr.split(":").map(Number);
            return hours * 60 + minutes;
          };

          const aTime = timeToMinutes(a.startTime);
          const bTime = timeToMinutes(b.startTime);

          // First sort by time
          if (aTime !== bTime) {
            return aTime - bTime;
          }

          // If times are equal, sort by periodName
          return a.periodName.localeCompare(b.periodName);
        });
      })
      .addCase(fetchPeriods.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSchoolDays.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSchoolDays.fulfilled, (state, action) => {
        state.loading = false;
        state.schoolDays = action.payload;
        console.log("state.schoolDays", state.schoolDays);
      })
      .addCase(fetchSchoolDays.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(saveSchoolDay.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveSchoolDay.fulfilled, (state, action) => {
        state.loading = false;
        state.schoolDays.push(action.payload);
      })
      .addCase(saveSchoolDay.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateSchoolDay.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSchoolDay.fulfilled, (state, action) => {
        state.loading = false;

        const index = state.schoolDays.findIndex(
          (day) => day.scDayID === action.payload.scDayID
        );
        if (index !== -1) {
          state.schoolDays[index] = action.payload;
        }
      })
      .addCase(updateSchoolDay.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteSchoolDay.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSchoolDay.fulfilled, (state, action) => {
        state.loading = false;
        state.schoolDays = state.schoolDays.filter(
          (day) => day.scDayID !== action.payload
        );
      })
      .addCase(deleteSchoolDay.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default schoolDaySlice.reducer;
